/**
 * Lightning.js
 * Chain lightning effect
 */

import { DesignDataTypes, Game, GameAnimatedSprite, GameScene, GameTimer } from "pixi_core";

import { Bubble } from "./Bubble";
import { Common } from "./Common";

import { GameEvents, SceneLayers, LIGHTNING_SEGMENT_SIZE, LIGHTNING_CLEAR_DELAY, BubblePopSource } from "../Constants";

export class Lightning {
    /**
     * @public
     * @param {GameScene} scene
     */
    constructor(scene) {
        /** @private @type {GameScene} */
        this._scene = scene;

        /** @private @type {Bubble} */
        this._startBubble = null;

        /** @private @type {Bubble[]} - All bubbles involved in the chain effect, including the start bubble */
        this._bubbles = [];

        /** @private @type {LightningSegment[]} */
        this._segments = [];

        /** @private @type {import("../Constants").LightningNode[]} */
        this._nodes = [];

        /** @private @type {Number} */
        this._nodesReached = 0;

        /** @private @type {GameTimer} */
        this._timerPopDelay = null;
        /** @private @type {GameTimer} */
        this._timerFadeDelay = null;
        /** @private @type {GameTimer} */
        this._timerClearDelay = null;
    }

    /**
     * @public
     * @param {Bubble} startBubble
     * @param {Bubble[]} bubbles
     */
    begin(startBubble, bubbles) {
        this._startBubble = startBubble;
        this._bubbles = bubbles;

        this._nodes = [];

        for (const bubble of this._bubbles) {
            this._nodes.push({
                bubble: bubble,
                distanceFromStart: 0,
                nextNodes: []
            });
        }

        this._analyzeNodes();

        this._nodesReached = 1;

        if (this._nodes.length > 1) {
            this._chain(this._nodes[0]);
        } else {
            this._popBubbles();
        }
    }

    /**
     * @private
     */
    _analyzeNodes() {
        // Distance from the start
        for (const node of this._nodes) {
            const deltaX = node.bubble.x - this._startBubble.x;
            const deltaY = node.bubble.y - this._startBubble.y;
            node.distanceFromStart = Math.sqrt(deltaX * deltaX + deltaY * deltaY);
        }

        this._nodes.sort(this._sortNodes);

        for (let i = 1; i < this._nodes.length; ++i) {
            const node = this._nodes[i];

            // Find closest visited node
            let closest = null;
            let minDistance2 = Infinity;
            for (let j = 0; j < i; ++j) {
                const otherNode = this._nodes[j];

                const deltaX = otherNode.bubble.x - node.bubble.x;
                const deltaY = otherNode.bubble.y - node.bubble.y;
                const distance2 = deltaX * deltaX + deltaY * deltaY;

                if (distance2 < minDistance2) {
                    closest = otherNode;
                    minDistance2 = distance2;
                }
            }
            closest.nextNodes.push(node);
        }
    }

    /**
     * @private
     * @param {import("../Constants").LightningNode} nodeA
     * @param {import("../Constants").LightningNode} nodeB
     */
    _sortNodes(nodeA, nodeB) {
        if (nodeA.distanceFromStart < nodeB.distanceFromStart) return -1;
        if (nodeA.distanceFromStart > nodeB.distanceFromStart) return 1;
        return 0;
    }

    /**
     * @private
     * @param {import("../Constants").LightningNode} node
     */
    _chain(node) {
        if (node.nextNodes.length === 0) {
            return;
        }

        for (const nextNode of node.nextNodes) {
            this._segments.push(new LightningSegment(this._scene, node, nextNode, this._onNodeReached.bind(this)));
        }
    }

    /**
     * A lightning segment has reached its target bubble
     * @private
     * @param {import("../Constants").LightningNode} targetNode
     */
    _onNodeReached(targetNode) {
        ++this._nodesReached;

        this._chain(targetNode);

        if (this._nodesReached === this._nodes.length) {
            // All bubbles have been reached
            // ==> schedule bubble pop and lightning fade effect

            this._timerPopDelay = Game.windows.getCurrentWindow().createTimer(Common.settings.bubbles.lightningPopDelay);
            this._timerPopDelay.onComplete = () => {
                this._popBubbles();
            };

            this._timerFadeDelay = Game.windows.getCurrentWindow().createTimer(Common.settings.bubbles.lightningFadeDelay);
            this._timerFadeDelay.onComplete = () => {
                this._fadeEffect();
            };
        }
    }

    /**
     * @private
     */
    _popBubbles() {
        for (const bubble of this._bubbles) {
            bubble.pop(true, BubblePopSource.eLIGHTNING);
        }

        Game.fire(GameEvents.eLIGHTNING_BUBBLES_CLEARED, this._bubbles);
    }

    /**
     * @private
     */
    _fadeEffect() {
        for (const segment of this._segments) {
            segment.fade();
        }

        this._timerClearDelay = Game.windows.getCurrentWindow().createTimer(LIGHTNING_CLEAR_DELAY);
        this._timerClearDelay.onComplete = () => {
            this.clear();
        };
    }

    /**
     * @public
     */
    clear() {
        this._startBubble = null;
        this._bubbles = [];
        this._nodes = [];
        this._nodesReached = 0;

        if (this._timerPopDelay) {
            this._timerPopDelay.destroy();
            this._timerPopDelay = null;
        }

        if (this._timerFadeDelay) {
            this._timerFadeDelay.destroy();
            this._timerFadeDelay = null;
        }

        if (this._timerClearDelay) {
            this._timerClearDelay.destroy();
            this._timerClearDelay = null;
        }

        for (const segment of this._segments) {
            segment.destroy();
        }
        this._segments = [];
    }
}

export class LightningSegment {
    /**
     * @public
     * @param {GameScene} scene
     * @param {import("../Constants").LightningNode} startNode
     * @param {import("../Constants").LightningNode} targetNode
     * @param {Function} callback
     */
    constructor(scene, startNode, targetNode, callback) {
        /** @private @type {GameScene} */
        this._scene = scene;

        /** @private @type {import("../Constants").LightningNode} */
        this._targetNode = targetNode;

        /** @private @type {Bubble} */
        this._startBubble = startNode.bubble;

        /** @private @type {Bubble} */
        this._targetBubble = targetNode.bubble;

        /** @private @type {Object[]} */
        this._segments = [];
        /** @private @type {Number} */
        this._segmentIndex = 0;

        /** @private @type {Function} */
        this._callback = callback;

        // Distance to target (pixels)
        const deltaX = this._targetBubble.x - this._startBubble.x;
        const deltaY = this._targetBubble.y - this._startBubble.y;
        const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);

        /** @private @type {Number} - Lightning line rotation/orientation */
        this._rotation = Math.atan2(deltaY, deltaX);
        const cosRotation = Math.cos(this._rotation);
        const sinRotation = Math.sin(this._rotation);

        const nbSegments = Math.ceil(distance / LIGHTNING_SEGMENT_SIZE);
        const segmentLength = distance / nbSegments;

        // Create segments
        let posX = this._startBubble.x;
        let posY = this._startBubble.y;
        for (let i = 0; i < nbSegments; ++i) {
            this._segments.push({
                x: posX,
                y: posY,
                length: segmentLength,
                fx: null
            });

            posX = posX + cosRotation * segmentLength;
            posY = posY + sinRotation * segmentLength;
        }

        this._nextSegment();
    }

    /**
     * @private
     */
    _nextSegment() {
        if (this._segmentIndex >= this._segments.length) {
            this._callback(this._targetNode);
            return;
        }

        Game.audio.playSfx("IDSND_BUB_LIGHTNING_PATH");
        const segment = this._segments[this._segmentIndex];

        // Travel speed (pixels/sec)
        let speed =
            Common.settings.bubbles.lightningSpeed -
            Common.settings.bubbles.lightningSpeedRandomRange +
            Math.random() * 2 * Common.settings.bubbles.lightningSpeedRandomRange;

        // Travel duration to target (sec)
        const duration = segment.length / speed;

        const animationData = Game.design.getData("Lightning", DesignDataTypes.eGRAPHIC);
        segment.fx = new GameAnimatedSprite(null, animationData, this._scene, SceneLayers.eBUBBLE_OVER);
        segment.fx.x = segment.x;
        segment.fx.y = segment.y;
        segment.fx.playAnimation("Start", false, { durationOverride: duration });

        segment.fx.rotation = this._rotation;

        const segmentScale = segment.length / LIGHTNING_SEGMENT_SIZE;
        segment.fx.scale.x = segmentScale;

        segment.fx.onComplete = () => {
            segment.fx.onComplete = null;
            segment.fx.playAnimation("Idle", true);

            ++this._segmentIndex;
            this._nextSegment();
        };
    }

    /**
     * @public
     */
    fade() {
        for (const segment of this._segments) {
            if (segment.fx) {
                segment.fx.playAnimation("End", false);
                segment.fx.onComplete = () => {
                    segment.fx.visible = false;
                };
            }
        }
    }

    /**
     * @public
     */
    destroy() {
        this._targetNode = null;
        this._startBubble = null;
        this._targetBubble = null;

        for (const segment of this._segments) {
            if (segment.fx) {
                segment.fx.stop();
                segment.fx.destroy();
            }
        }
        this._segments = [];
    }
}
